import React from "react";

type Props = {
  vitals: {
    height: number;
    gender: string;
    birthday: string;
    activityLevel: string;
    activityFactor?: number;
    currentWeightLbs?: number;
  };
  activitySummary: { label: string; factor: number };
  openVitalsEditor: () => void;
  fmtHeightFtIn: (heightIn: number) => string;
  calcAge: (birthday: string) => number | null;
  isDemoMode: boolean;
  displayedWeightLbs: number | null;
  displayedWeightSourceLabel?: string | null;
};

export function MetabolismSection({
  vitals,
  activitySummary,
  openVitalsEditor,
  fmtHeightFtIn,
  calcAge,
  isDemoMode,
  displayedWeightLbs,
  displayedWeightSourceLabel,
}: Props) {
  const rows = [
    { label: "Height", value: fmtHeightFtIn(vitals.height) },
    {
      label: "Weight",
      value: Number.isFinite(displayedWeightLbs)
        ? `${Number(displayedWeightLbs).toFixed(1)} lbs`
        : "—",
    },
    { label: "Activity factor", value: (vitals.activityFactor ?? activitySummary.factor).toFixed(2) },
    { label: "Age", value: String(calcAge(vitals.birthday) ?? "—") },
    {
      label: "Sex",
      value:
        vitals.gender === "M"
          ? "Male"
          : vitals.gender === "F"
            ? "Female"
            : vitals.gender === "X"
              ? "Prefer not to say"
              : "—",
    },
    { label: "Activity", value: activitySummary.label },
  ];

  return (
    <section
      style={{ maxWidth: 720, width: "100%", display: "grid", gap: "1rem" }}
    >
      <div
        style={{
          border: "1px solid rgba(148,163,184,0.14)",
          borderRadius: 12,
          padding: "1rem 1.05rem",
          background: "rgba(255,255,255,0.015)",
          width: "100%",
          boxSizing: "border-box",
          display: "grid",
          gap: "0.55rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem" }}>
          <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "rgba(255,255,255,0.9)" }}>Metabolism</div>
          <button
            onClick={openVitalsEditor}
            disabled={isDemoMode}
            title={isDemoMode ? "Disabled in Demo" : ""}
            style={{
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              background: "transparent",
              color: "rgba(255,255,255,0.72)",
              fontSize: "0.82rem",
              padding: "0.3rem 0.62rem",
              cursor: isDemoMode ? "not-allowed" : "pointer",
              fontWeight: 700,
              opacity: isDemoMode ? 0.5 : 1,
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: 0 }}>
        {rows.map((row, i) => (
          <div
            key={row.label}
            style={{
              display: "grid",
              gridTemplateColumns: "110px 1fr",
              gap: "0.5rem",
              alignItems: "baseline",
              padding: "0.55rem 0",
              borderBottom: i < rows.length - 1
                ? "1px solid rgba(255,255,255,0.05)"
                : "none",
            }}
          >
            <span style={{
              color: "rgba(255,255,255,0.45)",
              fontSize: "0.82rem",
              fontWeight: 500,
            }}>
              {row.label}
            </span>
            <span style={{
              color: "rgba(255,255,255,0.88)",
              fontSize: "0.95rem",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.45rem",
            }}>
              {row.value}
              {row.label === "Weight" && displayedWeightSourceLabel ? (
                <span
                  style={{
                    fontSize: "0.68rem",
                    lineHeight: 1,
                    padding: "0.2rem 0.38rem",
                    borderRadius: 999,
                    border: "1px solid rgba(125,211,252,0.35)",
                    background: "rgba(56,189,248,0.12)",
                    color: "rgba(186,230,253,0.95)",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayedWeightSourceLabel}
                </span>
              ) : null}
            </span>
          </div>
        ))}
        </div>
        <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>
          Used to estimate BMR/TDEE and calorie targets.
        </div>
      </div>
    </section>
  );
}
